import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { app } from '@microsoft/teams-js';
import { toTeamsTheme, type TeamsThemeName } from '@/lib/teams/theme';

/**
 * Teams host detection and context.
 *
 * The same bundle must run as a Teams personal tab and as a plain web app, so
 * initialization is best-effort: if the SDK does not answer within a short
 * window we assume a browser and continue without throwing.
 */

export type TeamsStatus = 'initializing' | 'in-teams' | 'browser' | 'error';

export interface TeamsUserInfo {
  /** Entra object id (`context.user.id`). */
  objectId: string | null;
  /** UPN, typically the corporate email. */
  userPrincipalName: string | null;
  loginHint: string | null;
  tenantId: string | null;
}

export interface TeamsContextValue {
  status: TeamsStatus;
  /** True only when the SDK confirmed we are hosted by a Teams client. */
  isInTeams: boolean;
  isReady: boolean;
  theme: TeamsThemeName;
  locale: string;
  user: TeamsUserInfo;
  /** `web` | `desktop` | `android` | `ios` when known. */
  hostClientType: string | null;
  /** Entry point subtitle, e.g. the channel/chat id when relevant. */
  frameContext: string | null;
  error: Error | null;
}

const EMPTY_USER: TeamsUserInfo = {
  objectId: null,
  userPrincipalName: null,
  loginHint: null,
  tenantId: null,
};

const DEFAULT_VALUE: TeamsContextValue = {
  status: 'initializing',
  isInTeams: false,
  isReady: false,
  theme: 'default',
  locale: 'es-MX',
  user: EMPTY_USER,
  hostClientType: null,
  frameContext: null,
  error: null,
};

const TeamsContext = createContext<TeamsContextValue>(DEFAULT_VALUE);

/** Milliseconds we are willing to wait for the Teams host to answer. */
const INITIALIZE_TIMEOUT_MS = 2500;

/**
 * Cheap pre-check: outside an iframe (and without the desktop bridge) there is
 * no Teams host, so we skip `app.initialize()` entirely and boot faster.
 */
function couldBeHostedByTeams(): boolean {
  if (typeof window === 'undefined') return false;
  const isFramed = window.parent !== window.self || Boolean(window.opener);
  const hasNativeBridge = 'nativeInterface' in window;
  const forced = new URLSearchParams(window.location.search).has('inTeams');
  return isFramed || hasNativeBridge || forced;
}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = window.setTimeout(
      () => reject(new Error('El cliente de Microsoft Teams no respondió a tiempo.')),
      ms,
    );
    promise.then(
      (value) => {
        window.clearTimeout(timer);
        resolve(value);
      },
      (error: unknown) => {
        window.clearTimeout(timer);
        reject(error instanceof Error ? error : new Error(String(error)));
      },
    );
  });
}

export function TeamsProvider({ children }: { children: ReactNode }): JSX.Element {
  const [value, setValue] = useState<TeamsContextValue>(DEFAULT_VALUE);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;

    const applyContext = (context: app.Context): void => {
      if (!mountedRef.current) return;
      setValue({
        status: 'in-teams',
        isInTeams: true,
        isReady: true,
        theme: toTeamsTheme(context.app.theme),
        locale: context.app.locale || 'es-MX',
        user: {
          objectId: context.user?.id ?? null,
          userPrincipalName: context.user?.userPrincipalName ?? null,
          loginHint: context.user?.loginHint ?? null,
          tenantId: context.user?.tenant?.id ?? null,
        },
        hostClientType: context.app.host?.clientType ?? null,
        frameContext: context.page?.frameContext ?? null,
        error: null,
      });
    };

    const runBrowserFallback = (error: Error | null): void => {
      if (!mountedRef.current) return;
      setValue({
        ...DEFAULT_VALUE,
        status: 'browser',
        isInTeams: false,
        isReady: true,
        // Outside Teams the theme provider falls back to the OS preference.
        error,
      });
    };

    if (!couldBeHostedByTeams()) {
      runBrowserFallback(null);
      return () => {
        mountedRef.current = false;
      };
    }

    void (async () => {
      try {
        await withTimeout(app.initialize(), INITIALIZE_TIMEOUT_MS);
        const context = await withTimeout(app.getContext(), INITIALIZE_TIMEOUT_MS);
        applyContext(context);

        app.registerOnThemeChangeHandler((theme: string) => {
          if (!mountedRef.current) return;
          setValue((previous) => ({ ...previous, theme: toTeamsTheme(theme) }));
        });

        // Tells the host to hide its own loading indicator.
        app.notifySuccess();
      } catch {
        // Not hosted by Teams (or the host is unreachable): degrade to browser.
        runBrowserFallback(null);
      }
    })();

    return () => {
      mountedRef.current = false;
    };
  }, []);

  const memoized = useMemo(() => value, [value]);

  return <TeamsContext.Provider value={memoized}>{children}</TeamsContext.Provider>;
}

export function useTeams(): TeamsContextValue {
  return useContext(TeamsContext);
}
